import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { V11UserAvatar } from './V11UserAvatar';
import { V11UserDropdown } from './V11UserDropdown';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface V11UserMenuProps {
  user?: {
    name: string;
    email: string;
    avatar?: string;
  };
  onLogout?: () => void;
}

export const V11UserMenu: React.FC<V11UserMenuProps> = ({
  user: propUser,
  onLogout
}) => {
  const { userData } = useUserProfile();
  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const user = propUser || userData;
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(prev => !prev);
  };

  const closeDropdown = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div className="relative hidden lg:block" ref={menuRef}>
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-between w-full gap-2 rounded-lg border border-v11-border bg-v11-surface px-3 py-2 text-sm font-medium text-v11-text-primary hover:bg-v11-surface-hover transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center gap-2">
          <V11UserAvatar
            src={user.avatar}
            name={user.name}
            size="sm"
          />

          <span className="hidden sm:block max-w-24 truncate">
            {user.name}
          </span>
        </div>

        <ChevronDown
          className={`h-3 w-3 text-v11-text-muted transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <V11UserDropdown
        userName={user.name}
        userEmail={user.email}
        isOpen={isOpen}
        onClose={closeDropdown}
        onLogout={onLogout || handleLogout}
        placement="top"
      />
    </div>
  );
};
